import React from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

// Images
import authLevel1 from '../assets/auth_level_1.jpg';
import authLevel2 from '../assets/auth_level_2.jpg';
import authLevel3 from '../assets/auth_level_3.jpg';
import ptwCold from '../assets/ptw_cold.jpg';
import ptwConfined from '../assets/ptw_confined.jpg';
import ptwElectrical from '../assets/ptw_electrical.jpg';
import emailNotif from '../assets/email_notif.png';

const FlowGuard = () => {
    const navigate = useNavigate();
    const { scrollYProgress } = useScroll();
    const [activeLevel, setActiveLevel] = useState(0); // 0: Level 1, 1: Level 2, 2: Level 3

    // Parallax effects
    const yHero = useTransform(scrollYProgress, [0, 0.2], [0, -50]);

    // Handle card cycling
    const handleCardClick = () => {
        setActiveLevel((prev) => (prev + 1) % 3);
    };

    const features = [
        {
            title: "Multi-Layer Authorization",
            description: "Secure, hierarchical approval workflows ensuring rigorous safety compliance.",
            icon: (
                <svg className="w-12 h-12 mb-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )
        },
        {
            title: "Multi PTW Types",
            description: "Dedicated workflows for Cold Work, Confined Space, and Electrical operations.",
            icon: (
                <svg className="w-12 h-12 mb-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 19.5C4 18.837 4.537 18.3 5.2 18.3H18.8C19.463 18.3 20 18.837 20 19.5C20 20.163 19.463 20.7 18.8 20.7H5.2C4.537 20.7 4 20.163 4 19.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4 12C4 11.337 4.537 10.8 5.2 10.8H18.8C19.463 10.8 20 11.337 20 12C20 12.663 19.463 13.2 18.8 13.2H5.2C4.537 13.2 4 12.663 4 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4 4.5C4 3.837 4.537 3.3 5.2 3.3H18.8C19.463 3.3 20 3.837 20 4.5C20 5.163 19.463 5.7 18.8 5.7H5.2C4.537 5.7 4 5.163 4 4.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )
        },
        {
            title: "Real-time Notification",
            description: "Instant email alerts keeping stakeholders informed at every approval stage.",
            icon: (
                <svg className="w-12 h-12 mb-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-vintage-beige text-black font-sans selection:bg-black selection:text-white">
            {/* Top-left HOME button - Vertical text */}
            <motion.button
                onClick={() => {
                    navigate('/');
                }}
                className="fixed top-0 left-0 z-50 flex flex-col items-center group hidden md:flex"
                style={{
                    padding: '20px'
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                <motion.span
                    className="text-black text-sm font-medium"
                    style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        transform: 'rotate(180deg)'
                    }}
                    whileHover={{ letterSpacing: "0.2em" }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                >
                    HOME
                </motion.span>
                {/* Vertical line below HOME text */}
                <motion.div
                    className="w-[1px] bg-black"
                    initial={{ height: '80px', opacity: 0.6 }}
                    whileHover={{ height: '100px', opacity: 1 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    style={{
                        marginTop: '20px'
                    }}
                />
            </motion.button>

            {/* 2025 Vertical Text */}
            <div className="fixed left-8 bottom-8 z-50 hidden md:block">
                <span
                    className="text-black/40 text-[10px] font-sans tracking-widest block"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                >
                    © 2025
                </span>
            </div>
            {/* Hero Section */}
            <section className="min-h-screen flex flex-col justify-start md:justify-center items-center px-6 md:px-20 relative overflow-hidden pt-4 md:pt-0">
                {/* Mobile Top Bar - HOME + Line */}
                <div className="md:hidden flex items-center mb-12 w-full gap-4">
                    <button
                        onClick={() => navigate('/')}
                        className="text-black text-sm font-medium tracking-widest uppercase"
                    >
                        HOME
                    </button>
                    <div className="h-[1px] bg-black w-24 opacity-20"></div>
                </div>
                <motion.div
                    style={{ y: yHero }}
                    className="text-center z-10"
                >
                    <motion.h1
                        className="text-[15vw] sm:text-[12vw] md:text-[10vw] leading-[0.8] font-serif font-normal"
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
                    >
                        FlowGuard
                    </motion.h1>
                    <motion.p
                        className="mt-8 text-lg md:text-2xl font-light tracking-wide max-w-2xl mx-auto opacity-80"
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
                    >
                        A robust Permit To Work System engineered for safety and compliance.
                    </motion.p>
                </motion.div>
            </section>

            {/* Story Section */}
            <section className="py-12 md:py-24 px-6 md:px-20 md:pr-40">
                <div className="flex flex-col md:grid md:grid-cols-2 gap-12 md:gap-16 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-4xl md:text-6xl font-serif mb-6 md:mb-8">Concept</h2>
                        <p className="text-base md:text-xl leading-relaxed text-gray-700">
                            FlowGuard represents a paradigm shift in industrial safety management. It digitizes the traditional paper-based permit system, creating a transparent, auditable, and efficient workflow.
                        </p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="bg-black/5 p-8 md:p-12 rounded-lg"
                    >
                        <p className="text-xl md:text-2xl font-light italic leading-relaxed text-black/80">
                            "Minimalist design meets maximum security. A system that doesn't just manage permits, but actively safeguards processes."
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Features Horizontal Scroll or Grid */}
            <section className="py-12 md:py-24 px-6 md:px-20 bg-black text-white rounded-t-[3rem]">
                <div className="mb-20">
                    <h2 className="text-4xl md:text-6xl font-serif mb-4">Core Features</h2>
                    <div className="w-full h-[1px] bg-white/20"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            className="group p-8 border border-white/10 rounded-2xl hover:bg-white/5 transition-colors duration-500 cursor-default"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                        >
                            <div className="text-white/80 group-hover:text-white transition-colors duration-500">
                                {feature.icon}
                            </div>
                            <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                            <p className="text-gray-400 group-hover:text-gray-200 leading-relaxed">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Showcase / Detail Sections */}

            {/* 1. Multi-layer Auth */}
            <section className="py-12 md:py-32 px-6 md:px-20">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-16 md:gap-12">
                    <div className="w-full md:w-1/2 flex justify-center order-2 md:order-1">
                        <motion.div
                            className="relative h-[400px] md:h-[500px] w-full max-w-sm md:max-w-md perspective-1000 cursor-pointer"
                            onClick={handleCardClick}
                            viewport={{ once: true }}
                        >
                            {/* Level 1 - Application */}
                            <motion.div
                                className="absolute top-0 left-0 w-full h-[250px] md:h-[300px] rounded-xl shadow-2xl overflow-hidden border border-gray-200 bg-white"
                                animate={{
                                    y: activeLevel === 0 ? 0 : activeLevel === 1 ? 20 : 40,
                                    scale: activeLevel === 0 ? 1 : activeLevel === 1 ? 0.95 : 0.9,
                                    rotateX: activeLevel === 0 ? 0 : activeLevel === 1 ? -2 : -5,
                                    opacity: activeLevel === 0 ? 1 : activeLevel === 1 ? 0.9 : 0.8,
                                    zIndex: activeLevel === 0 ? 30 : activeLevel === 1 ? 20 : 10
                                }}
                                transition={{ duration: 0.6, ease: "backOut" }}
                            >
                                <img src={authLevel1} alt="Auth Level 1" className="w-full h-full object-cover" />
                                <div className="absolute bottom-0 left-0 w-full bg-white/90 p-2 text-[10px] md:text-xs font-bold text-center backdrop-blur-sm">Level 1: Application</div>
                            </motion.div>

                            {/* Level 2 - Verification */}
                            <motion.div
                                className="absolute top-0 left-0 w-full h-[250px] md:h-[300px] rounded-xl shadow-xl overflow-hidden border border-gray-200 bg-white"
                                animate={{
                                    y: activeLevel === 1 ? 0 : activeLevel === 2 ? 20 : 40,
                                    scale: activeLevel === 1 ? 1 : activeLevel === 2 ? 0.95 : 0.9,
                                    rotateX: activeLevel === 1 ? 0 : activeLevel === 2 ? -2 : -5,
                                    opacity: activeLevel === 1 ? 1 : activeLevel === 2 ? 0.9 : 0.8,
                                    zIndex: activeLevel === 1 ? 30 : activeLevel === 2 ? 20 : 10
                                }}
                                transition={{ duration: 0.6, ease: "backOut" }}
                            >
                                <img src={authLevel2} alt="Auth Level 2" className="w-full h-full object-cover" />
                                <div className="absolute bottom-0 left-0 w-full bg-white/90 p-2 text-[10px] md:text-xs font-bold text-center backdrop-blur-sm">Level 2: Verification</div>
                            </motion.div>

                            {/* Level 3 - Approval */}
                            <motion.div
                                className="absolute top-0 left-0 w-full h-[250px] md:h-[300px] rounded-xl shadow-xl overflow-hidden border border-gray-200 bg-white"
                                animate={{
                                    y: activeLevel === 2 ? 0 : activeLevel === 0 ? 20 : 40,
                                    scale: activeLevel === 2 ? 1 : activeLevel === 0 ? 0.95 : 0.9,
                                    rotateX: activeLevel === 2 ? 0 : activeLevel === 0 ? -2 : -5,
                                    opacity: activeLevel === 2 ? 1 : activeLevel === 0 ? 0.9 : 0.8,
                                    zIndex: activeLevel === 2 ? 30 : activeLevel === 0 ? 20 : 10
                                }}
                                transition={{ duration: 0.6, ease: "backOut" }}
                            >
                                <img src={authLevel3} alt="Auth Level 3" className="w-full h-full object-cover" />
                                <div className="absolute bottom-0 left-0 w-full bg-white/90 p-2 text-[10px] md:text-xs font-bold text-center backdrop-blur-sm">Level 3: Approval</div>
                            </motion.div>
                        </motion.div>
                    </div>
                    <div className="w-full md:w-1/2 order-1 md:order-2">
                        <h3 className="text-3xl md:text-4xl font-serif mb-6">Multi-Layer Authorization</h3>
                        <p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-xl">
                            Security isn't flat. FlowGuard implements a hierarchical approval model where permits must pass through specific authorization gates—from supervisors to safety officers—ensuring no check is skipped.
                        </p>
                    </div>
                </div>
            </section>

            {/* 2. PTW Types */}
            <section className="py-16 md:py-32 px-6 md:px-20 bg-white">
                <PTWSection />
            </section>

            {/* 3. Email Notification */}
            <section className="py-12 md:py-32 px-6 md:px-20 overflow-hidden bg-gray-50">
                <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
                    <div className="w-full md:w-3/5 flex justify-center order-2 md:order-1">
                        <motion.div
                            className="relative w-full max-w-[320px] sm:max-w-[400px] md:max-w-[500px]"
                            initial={{ y: 50, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.8 }}
                        >
                            {/* Device Frame */}
                            <motion.div
                                className="relative z-10 rounded-[2.5rem] md:rounded-[3rem] border-4 md:border-8 border-black overflow-hidden shadow-2xl bg-white"
                                animate={{ y: [0, -15, 0] }}
                                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                            >
                                <img src={emailNotif} alt="Email Notification" className="w-full h-auto object-cover block scale-110" />
                            </motion.div>

                            {/* Glow/Shadow Effect */}
                            <motion.div
                                className="absolute -bottom-8 md:-bottom-10 left-8 md:left-10 right-8 md:right-10 h-6 md:h-8 bg-black/10 blur-xl rounded-full"
                                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
                                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                            />
                        </motion.div>
                    </div>

                    <div className="w-full md:w-1/2 text-left order-1 md:order-2">
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className="inline-block mb-6 p-3 md:p-4 bg-black text-white rounded-xl md:rounded-2xl">
                                <svg width="24" height="24" className="md:w-8 md:h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-3xl md:text-5xl font-serif mb-6">Stay Connected</h3>
                            <p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-xl">
                                Automated email notifications ensure that the right people know the right information at the right time. No delays, no lost paperwork.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Next Project / Footer */}
            <section className="py-20 px-6 md:px-20 border-t border-black/10 text-center">
                <button
                    onClick={() => navigate('/work')}
                    className="text-xl md:text-3xl font-bold hover:tracking-widest transition-all duration-300"
                >
                    Back to All Works
                </button>
            </section>
        </div >
    );
};

const PTWSection = () => {
    const [activeType, setActiveType] = useState('Cold Work');

    const types = [
        { name: 'Cold Work', image: ptwCold, color: 'bg-blue-50', borderColor: 'border-blue-500' },
        { name: 'Confined Space', image: ptwConfined, color: 'bg-orange-50', borderColor: 'border-orange-500' },
        { name: 'Electrical Work', image: ptwElectrical, color: 'bg-red-50', borderColor: 'border-red-500' }
    ];

    return (
        <div className="flex flex-col-reverse md:flex-row items-center gap-12">
            <div className="w-full md:w-1/2">
                <h3 className="text-4xl font-serif mb-6">Flexible PTW Types</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    Different risks require different protocols. Hover over the types to visualize the specific workflows.
                </p>
                <div className="space-y-4">
                    {types.map((type, i) => (
                        <motion.div
                            key={type.name}
                            className={`p-6 border-l-4 ${activeType === type.name ? 'border-black bg-gray-100' : 'border-gray-200 bg-white'} cursor-pointer transition-all duration-300 shadow-sm`}
                            initial={{ x: -20, opacity: 0 }}
                            whileInView={{ x: 0, opacity: 1 }}
                            transition={{ delay: i * 0.1 }}
                            onMouseEnter={() => setActiveType(type.name)}
                            onClick={() => setActiveType(type.name)}
                        >
                            <span className={`font-bold text-lg ${activeType === type.name ? 'text-black' : 'text-gray-500'}`}>{type.name}</span>
                        </motion.div>
                    ))}
                </div>
            </div>
            <div className="w-full md:w-1/2 h-[400px] relative rounded-2xl overflow-hidden shadow-2xl bg-gray-200">
                <AnimatePresence mode="wait">
                    {types.map((type) => (
                        activeType === type.name && (
                            <motion.img
                                key={type.name}
                                src={type.image}
                                alt={type.name}
                                className="absolute top-0 left-0 w-full h-full object-cover"
                                initial={{ opacity: 0, scale: 1.1 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                            />
                        )
                    ))}
                </AnimatePresence>
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
                <div className="absolute bottom-6 left-6 text-white font-bold text-xl pointer-events-none">
                    {activeType} Permit
                </div>
            </div>
        </div>
    );
};

export default FlowGuard;
